import { Avatar, AvatarGroup, Box, Button, Chip, Skeleton, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { GoEvent } from "../../../types";
import { useEventDataFormatter } from "../../../hooks/useEventDataFormatter";
import { colors } from "../../../theme/palette";

import { EventDetailsDateIcon, EventDetailsLocationIcon } from "../../../assets/icons/MUI/EventsIcons";
import { CircularProgressWithLabel } from "../../../shared/UI/CircularProgressWithLabel";

import ConfirmCancelEventPopUp from "./ConfirmCancelEventPopUp";
import { getEventRoute } from "../../../routers/routeUtils";
import { useCancelEvent } from "../../../api/queries/events/eventsListMutations";

const EventCard = ({ event, userId }: { event: GoEvent; userId?: number | null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isCanceled, setIsCanceled] = useState(false);
  const [isCancelEventPopUpOpen, setIsCancelEventPopUpOpen] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const [cancelEventComment, setCancelEventComment] = useState<string | null>(null);

  const {
    id,
    name,
    totalPlayersInvitedNum,
    acceptedPlayersNum,
    startingDay,
    startingMonth,
    startingHour,
    endHour,
    eventPrivacy,
    eventType,
    status,
    eventStatus,
    eventLocation,
    invitedPlayersAvatars,
    authorId,
    authorName,
    authorMedia,
    authorFirstTwoChr,
  } = useEventDataFormatter(event);

  const { mutate: cancelEventMutation } = useCancelEvent(
    id,
    () => {
      setIsCanceled(true);
      setIsCancelEventPopUpOpen(false);
    },
    () => {
      setIsCancelEventPopUpOpen(false);
    }
  );

  const handleCancelEvent = () => {
    const body = {
      cancelEventReason: cancelReason?.title,
      cancelEventComment,
    };
    cancelEventMutation(body);
  };

  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: `1px solid ${colors.Gray100}`,
        background: colors.White,
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        rowGap: "16px",
      }}
    >
      <Box sx={{ display: "flex", columnGap: "12px" }}>
        <Chip
          label={isCanceled ? t("pages.events.eventList.CANCELLED") : eventType}
          sx={{
            padding: "2px 12px",
            minWidth: "64px",
            height: "fit-content",
            color: colors.Dark1000,
            background: colors.SecondaryMint,
            "& .MuiChip-label": {
              padding: 0,
            },
          }}
        />
        <Chip
          label={eventStatus}
          sx={{
            padding: "2px 12px",
            minWidth: "64px",
            height: "fit-content",
            color: colors.White,
            background:
              status === "FINISHED"
                ? colors.Dark600
                : status === "SOON"
                ? colors.Yellow200
                : status === "ACTIVE"
                ? colors.Red100
                : colors.Dark600,
            "& .MuiChip-label": {
              padding: 0,
            },
          }}
        />
      </Box>
      <Box
        sx={{
          paddingBottom: "16px",
          borderBottom: `1px solid ${colors.Gray100}`,
        }}
      >
        <Typography variant="body16ExtraBold" sx={{ color: colors.Dark1000, marginBottom: "8px" }}>
          {name}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", maxWidth: "calc(100% - 56px)" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                paddingInlineEnd: "16px",
                marginInlineEnd: "16px",
                borderRight: `1px solid ${colors.Gray100}`,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h4ExtraBold" sx={{ color: colors.Green100 }}>
                {startingDay}
              </Typography>
              <Typography variant="h4ExtraBold" sx={{ color: colors.Green100 }}>
                {startingMonth}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                rowGap: "11px",
                flexDirection: "column",
                paddingInlineEnd: "16px",
                marginInlineEnd: "16px",
                justifyContent: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", columnGap: "8px" }}>
                <EventDetailsLocationIcon />
                <Typography
                  variant="body12Regular"
                  sx={{
                    color: colors.Dark800,
                    maxWidth: "200px",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    wordBreak: "break-word",
                  }}
                >
                  {eventLocation}
                </Typography>
              </Box>

              <Typography
                variant="body12Regular"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  columnGap: "8px",
                  color: colors.Dark800,
                }}
              >
                <EventDetailsDateIcon />
                {startingHour} - {endHour} . {eventPrivacy}
              </Typography>
            </Box>
          </Box>
          <CircularProgressWithLabel value={acceptedPlayersNum} totalNum={totalPlayersInvitedNum} />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "6px" }}>
          <Typography variant="body12SemiBold" sx={{ color: colors.Dark600 }}>
            {t("pages.events.eventList.organizer")}
          </Typography>
          <Box sx={{ display: "flex", columnGap: "8px", alignItems: "center" }}>
            <Avatar
              src={authorMedia}
              sx={{
                width: "24px",
                height: "24px",
                fontSize: "10px",
              }}
            >
              {authorFirstTwoChr}
            </Avatar>
            <Typography
              variant="body12SemiBold"
              sx={{
                color: colors.Dark1000,
                fontWeight: 700,
                display: "flex",
                columnGap: "8px",
                alignItems: "center",
              }}
            >
              {authorName}
            </Typography>
          </Box>
        </Box>
        <AvatarGroup
          sx={{
            "& .MuiAvatar-circular": {
              fontSize: "12px",
              width: "24px",
              height: "24px",
            },
          }}
          total={acceptedPlayersNum}
        >
          <AvatarGroup total={invitedPlayersAvatars.length}>
            {invitedPlayersAvatars.slice(0, 4).map((player: any, idx: number) => {
              const { name, media, firstTwoChr } = player;
              return (
                <Avatar key={idx} alt={name} src={media}>
                  {firstTwoChr}
                </Avatar>
              );
            })}
          </AvatarGroup>
        </AvatarGroup>
      </Box>
      <Box sx={{ display: "flex", columnGap: "16px" }}>
        {userId !== authorId || status === "FINISHED" || status === "CANCELLED" || isCanceled ? (
          <Button variant="primary-sm" fullWidth onClick={() => navigate(getEventRoute(id))}>
            {t("pages.events.eventList.viewDetails")}
          </Button>
        ) : (
          <>
            <Button variant="primary-sm" sx={{ width: "50%" }} onClick={() => navigate(getEventRoute(id))}>
              {t("pages.events.eventList.managePlayers")}
            </Button>
            <Button
              variant="outlined-sm"
              sx={{
                width: "50%",
                borderColor: colors.Red200,
                color: colors.Red200,
                "&:hover": {
                  backgroundColor: colors.Red25,
                  color: colors.Red100,
                  borderColor: colors.Red100,
                },
              }}
              onClick={() => setIsCancelEventPopUpOpen(true)}
            >
              {t("pages.events.eventList.cancelMatch")}
            </Button>
          </>
        )}
      </Box>
      <ConfirmCancelEventPopUp
        openDialog={isCancelEventPopUpOpen}
        handleCloseDialog={() => setIsCancelEventPopUpOpen(false)}
        handleCancelEvent={handleCancelEvent}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        cancelEventComment={cancelEventComment}
        setCancelEventComment={setCancelEventComment}
      />
    </Box>
  );
};

export default EventCard;

export const EventCardLoading = () => {
  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: `1px solid ${colors.Gray100}`,
        background: colors.White,
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        rowGap: "16px",
      }}
    >
      {/* Top Chips */}
      <Box sx={{ display: "flex", columnGap: "12px" }}>
        <Skeleton variant="rounded" width="64px" height="20px" />
        <Skeleton variant="rounded" width="64px" height="20px" />
      </Box>

      {/* Event Name and Details */}
      <Box
        sx={{
          paddingBottom: "16px",
          borderBottom: `1px solid ${colors.Gray100}`,
        }}
      >
        <Typography variant="body16ExtraBold" sx={{ color: colors.Dark1000, marginBottom: "8px" }}>
          <Skeleton width="70%" />
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* Event Date */}
          <Box sx={{ display: "flex", maxWidth: "calc(100% - 56px)" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                paddingInlineEnd: "16px",
                marginInlineEnd: "16px",
                borderRight: `1px solid ${colors.Gray100}`,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Skeleton variant="text" width="40px" height="32px" />
              <Skeleton variant="text" width="40px" height="32px" />
            </Box>
            <Box
              sx={{
                display: "flex",
                rowGap: "11px",
                flexDirection: "column",
                paddingInlineEnd: "16px",
                marginInlineEnd: "16px",
                justifyContent: "center",
              }}
            >
              <Skeleton variant="text" width="120px" />
              <Skeleton variant="text" width="140px" />
            </Box>
          </Box>
          {/* Progress Circle */}
          <Skeleton variant="circular" width="48px" height="48px" />
        </Box>
      </Box>

      {/* Organizer and Players */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "6px" }}>
          <Skeleton width="56px" />
          <Box sx={{ display: "flex", columnGap: "8px", alignItems: "center" }}>
            <Skeleton variant="circular" width="24px" height="24px" />
            <Skeleton width="80px" />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            width: "60px",
            height: "24px",
          }}
        >
          {[...Array(5)].map((_, index) => (
            <Skeleton
              key={index}
              variant="circular"
              width={24}
              height={24}
              sx={{
                position: "absolute",
                left: `${index * 12}px`, // Adjust for offset overlap
                zIndex: 3 - index, // Adjust stacking
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Buttons */}
      <Box sx={{ display: "flex", columnGap: "16px" }}>
        <Skeleton variant="rounded" width="100%" height="40px" />
      </Box>
    </Box>
  );
};
